import { createActions, handleActions } from 'redux-actions';
import { destroy as destroyForm } from 'redux-form';
import Notifications from 'react-notification-system-redux';
import {
    defaultPaginationData,
    getPaginationAndOrderData,
} from '../../shared/utils';

const actions = createActions({
    CONTACTS: {
        LIST: {
            FILTER: filter => ({ filter }),
            FETCHING: currentPage => ({ currentPage }),
            LOADED: list => ({ list }),
        },
        EDIT: {
            FETCHING: contactId => ({ contactId }),
            LOADED: contact => ({ contact }),
        },
        UPDATE: {
            UPDATING: undefined,
            UPDATED: contact => ({ contact }),
        },
        ADD: {
            ADDING: undefined,
            ADDED: contact => ({ contact }),
        },
        REMOVE: {
            PROMPT: ({ contactId, contactName }) => ({
                contactId,
                contactName,
            }),
            REMOVING: undefined,
            REMOVED: undefined,
            RESET: undefined,
        },
    },
});

const initialState = Object.assign(
    {},
    {
        list: Object.assign(
            {},
            {
                filter: null,
                error: null,
                fetching: false,
                fetchingTime: null,
                contacts: null,
                currentPage: 1,
            },
            defaultPaginationData
        ),
        edit: {
            error: null,
            fetching: false,
            contact: null,
        },
        update: {
            error: null,
            updating: false,
        },
        add: {
            error: null,
            adding: false,
        },
        remove: {
            contactId: null,
            contactName: null,
            removing: false,
        },
    }
);

let fetchingTimer;

export default handleActions(
    {
        [actions.contacts.list.filter]: (state, action) => {
            return Object.assign({}, state, {
                list: Object.assign({}, state.list, {
                    filter: action.payload.filter,
                }),
            });
        },

        [actions.contacts.list.fetching]: (state, action) => {
            fetchingTimer = Date.now();

            return Object.assign({}, state, {
                list: Object.assign({}, state.list, {
                    fetching: true,
                    currentPage: action.payload.currentPage,
                }),
            });
        },

        [actions.contacts.list.loaded]: {
            next: (state, action) => {
                fetchingTimer = Date.now() - fetchingTimer;

                return Object.assign({}, state, {
                    list: Object.assign(
                        {},
                        state.list,
                        getPaginationAndOrderData(action.payload.list.meta),
                        {
                            contacts: action.payload.list.results.reduce(
                                (result, contact) => {
                                    result[contact.contactId] = {
                                        contactId: contact.contactId,
                                        contactEmail: contact.contactEmail,
                                        contactTitle: contact.contactTitle,
                                        contactForenames:
                                            contact.contactForenames,
                                        contactSurname: contact.contactSurname,
                                        contactTelephone:
                                            contact.contactTelephone,
                                    };
                                    return result;
                                },
                                {}
                            ),
                            fetching: false,
                            fetchingTime: fetchingTimer,
                        }
                    ),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    list: Object.assign({}, initialState.list, {
                        error: true,
                    }),
                });
            },
        },

        [actions.contacts.edit.fetching]: (state, action) => {
            return Object.assign({}, state, {
                edit: Object.assign({}, initialState.edit, {
                    fetching: true,
                }),
            });
        },

        [actions.contacts.edit.loaded]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    edit: Object.assign({}, initialState.edit, {
                        contact: Object.assign({}, action.payload.contact),
                    }),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    list: Object.assign({}, initialState.edit, {
                        error: true,
                    }),
                });
            },
        },

        [actions.contacts.update.updating]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.update, {
                    updating: true,
                }),
            });
        },

        [actions.contacts.update.updated]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    edit: Object.assign({}, initialState.edit, {
                        contact: action.payload.contact,
                    }),
                    update: Object.assign({}, initialState.update),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    update: Object.assign({}, initialState.update, {
                        error: true,
                    }),
                });
            },
        },

        [actions.contacts.add.adding]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.add, {
                    updating: true,
                }),
            });
        },

        [actions.contacts.add.added]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    add: Object.assign({}, initialState.add, {
                        contact: action.payload.contact,
                    }),
                    update: Object.assign({}, initialState.add),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    update: Object.assign({}, initialState.add, {
                        error: true,
                    }),
                });
            },
        },

        [actions.contacts.remove.prompt]: (state, action) => {
            const { contactId, contactName } = action.payload;

            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove, {
                    contactId,
                    contactName,
                }),
            });
        },

        [actions.contacts.remove.removing]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, state.remove, {
                    removing: true,
                }),
            });
        },

        [actions.contacts.remove.removed]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        [actions.contacts.remove.reset]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },
    },
    initialState
);

export const filterContacts = filter => {
    return actions.contacts.list.filter(filter);
};

export const loadContacts = (currentPage, limit = 20) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        const filter = getState().contacts.list.filter || '';

        dispatch(actions.contacts.list.fetching({ currentPage }));

        return api
            .get(
                `/contacts?limit=${limit}&page=${currentPage}&filter=${filter}`
            )
            .then(
                response =>
                    dispatch(actions.contacts.list.loaded(response.data)),
                err => dispatch(actions.contacts.list.loaded(err))
            );
    };
};

export const loadContactDetails = contactId => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.contacts.edit.fetching());

        return api
            .get(`/contacts/${contactId}`)
            .then(
                response =>
                    dispatch(actions.contacts.edit.loaded(response.data)),
                err => dispatch(actions.contacts.edit.loaded(err))
            );
    };
};

export const updateContactDetails = (contactId, contactData) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.contacts.update.updating());

        return api.put(`/contacts/${contactId}`, contactData).then(
            response => {
                dispatch(actions.contacts.update.updated(response.data));

                dispatch(
                    Notifications.success({
                        title: 'Contact updated',
                        message: 'The provided details have been saved.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.contacts.update.updated(err));
                dispatch(
                    Notifications.error({
                        title: 'Update failed',
                        message:
                            'An error occurred whilst saving this contact. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const addContactDetails = contactData => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.contacts.add.adding());

        return api.post(`/contacts`, contactData).then(
            response => {
                dispatch(actions.contacts.add.added(response.data));

                // this is needed to clear the 'dirty' flag and prevent warning that data may be lost
                dispatch(destroyForm('contactAddForm'));

                dispatch(
                    Notifications.success({
                        title: 'Contact created',
                        message: 'The provided details have been saved.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.contacts.add.added(err));
                dispatch(
                    Notifications.error({
                        title: 'Creation failed',
                        message:
                            'An error occurred whilst creating this contact. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const showDeleteContactPrompt = (
    contactId: string,
    contactName: string
) => {
    return actions.contacts.remove.prompt({ contactId, contactName });
};

export const deletePromptReset = () => {
    return actions.contacts.remove.reset();
};

export const removeContact = (contactId: string, contactName: string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.contacts.remove.removing());

        return api.delete(`/contacts/${contactId}`).then(
            response => {
                dispatch(actions.contacts.remove.removed());

                dispatch(
                    Notifications.success({
                        title: 'Contact deleted',
                        message: `The contact ${contactName} has been deleted.`,
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.contacts.remove.removed(err));
                dispatch(
                    Notifications.error({
                        title: 'Delete failed',
                        message:
                            'An error occurred whilst deleting this contact. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};



// WEBPACK FOOTER //
// ./src/redux/reducers/contacts.js